/**
 * Static command-palette / global-search menu entries.
 */

import { MenuSearchResult } from '@/types/search.types';

/** Static command-palette entries (view + create) keyed by RBAC resource. */
export const SEARCH_MENUS: MenuSearchResult[] = [
  {
    id: 'menu-dashboard',
    label: 'Dashboard',
    type: 'view',
    route: '/dashboard',
    icon: 'LayoutDashboard',
    keywords: ['dashboard', 'accueil', 'tableau de bord'],
    resource: 'dashboard',
  },
  {
    id: 'menu-calendar',
    label: 'Calendrier',
    type: 'view',
    route: '/calendar',
    icon: 'CalendarDays',
    keywords: ['calendrier', 'calendar', 'agenda'],
    resource: 'calendar',
  },
  {
    id: 'menu-sessions-view',
    label: 'Consulter les séances',
    type: 'view',
    route: '/sessions',
    icon: 'Calendar',
    keywords: ['séances', 'sessions', 'consulter', 'voir'],
    resource: 'sessions',
  },
  {
    id: 'menu-sessions-create',
    label: 'Planification des séances',
    type: 'create',
    route: '/sessions?create=true',
    icon: 'CalendarPlus',
    keywords: ['séance', 'session', 'créer', 'ajouter', 'nouvelle'],
    resource: 'sessions',
    requiresWrite: true,
  },
  {
    id: 'menu-reports-remote-view',
    label: 'Comptes rendus',
    type: 'view',
    route: '/reports',
    icon: 'FileText',
    keywords: ['comptes', 'rendus', 'cr', 'scoring', 'tutorat', 'remote', 'reports'],
    resource: 'reports',
  },
  {
    id: 'menu-assessments-view',
    label: 'Bilans',
    type: 'view',
    route: '/assessments',
    icon: 'ClipboardList',
    keywords: ['bilan', 'bilans', 'évaluation', 'nominatif', 'auto-évaluation'],
    resource: 'assessments',
  },
  {
    id: 'menu-assessments-create',
    label: 'Générer un bilan',
    type: 'create',
    route: '/assessments?create=true',
    icon: 'ClipboardList',
    keywords: ['bilan', 'générer', 'créer', 'nouveau'],
    resource: 'assessments',
    requiresWrite: true,
  },
  {
    id: 'menu-pairs-config',
    label: 'Configurer le matching',
    type: 'view',
    route: '/pairs',
    icon: 'Settings',
    keywords: ['matching', 'configurer', 'configuration', 'affecter', 'binômes'],
    resource: 'pairs',
  },
  {
    id: 'menu-pairs-list',
    label: 'Gestion des binômes',
    type: 'view',
    route: '/pairs',
    icon: 'Link',
    keywords: ['matching', 'binômes', 'gestion', 'liste', 'paires', 'affectations'],
    resource: 'pairs',
  },
  {
    id: 'menu-tutors-view',
    label: 'Consulter les tuteurs',
    type: 'view',
    route: '/tutors',
    icon: 'GraduationCap',
    keywords: ['tuteurs', 'tutors', 'consulter', 'voir'],
    resource: 'tutors',
  },
  {
    id: 'menu-tutors-create',
    label: 'Ajouter un tuteur',
    type: 'create',
    route: '/tutors?create=true',
    icon: 'UserPlus',
    keywords: ['tuteur', 'ajouter', 'créer', 'nouveau'],
    resource: 'tutors',
    requiresWrite: true,
  },
  {
    id: 'menu-students-view',
    label: 'Consulter les élèves',
    type: 'view',
    route: '/students',
    icon: 'Users',
    keywords: ['élèves', 'students', 'consulter', 'voir'],
    resource: 'students',
  },
  {
    id: 'menu-students-create',
    label: 'Ajouter un élève',
    type: 'create',
    route: '/students?create=true',
    icon: 'UserPlus',
    keywords: ['élève', 'étudiant', 'ajouter', 'créer', 'nouveau'],
    resource: 'students',
    requiresWrite: true,
  },
  {
    id: 'menu-parents-view',
    label: 'Consulter les parents',
    type: 'view',
    route: '/parents',
    icon: 'Users',
    keywords: ['parents', 'consulter', 'voir', 'famille'],
    resource: 'parents',
  },
  {
    id: 'menu-parents-create',
    label: 'Ajouter un parent',
    type: 'create',
    route: '/parents?create=true',
    icon: 'UserPlus',
    keywords: ['parent', 'ajouter', 'créer', 'nouveau', 'famille'],
    resource: 'parents',
    requiresWrite: true,
  },
  {
    id: 'menu-volunteers-view',
    label: 'Consulter les VSC',
    type: 'view',
    route: '/volunteers',
    icon: 'Heart',
    keywords: ['volunteers', 'volontaires', 'consulter', 'voir'],
    resource: 'volunteers',
  },
  {
    id: 'menu-volunteers-create',
    label: 'Ajouter un VSC',
    type: 'create',
    route: '/volunteers?create=true',
    icon: 'UserPlus',
    keywords: ['volunteer', 'vsc', 'volontaire', 'ajouter', 'créer', 'nouveau'],
    resource: 'volunteers',
    requiresWrite: true,
  },
  {
    id: 'menu-admins-view-admin',
    label: 'Consulter les administrateurs',
    type: 'view',
    route: '/admins?tab=admin',
    icon: 'Shield',
    keywords: ['administrateurs', 'admins', 'consulter', 'voir'],
    resource: 'admins',
    excludeIfSingleAdminTab: true,
  },
  {
    id: 'menu-admins-view-superadmin',
    label: 'Consulter les super-administrateurs',
    type: 'view',
    route: '/admins?tab=superadmin',
    icon: 'Crown',
    keywords: ['super', 'administrateurs', 'superadmin', 'consulter', 'voir'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'tab-admin' },
  },
  {
    id: 'menu-admins-view-coordinator',
    label: 'Consulter les coordinateurs',
    type: 'view',
    route: '/admins?tab=coordinator',
    icon: 'UserCog',
    keywords: ['coordinateurs', 'coordinators', 'consulter', 'voir'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'tab-coordinator' },
  },
  {
    id: 'menu-admins-view-manager',
    label: 'Consulter les managers',
    type: 'view',
    route: '/admins?tab=manager',
    icon: 'Briefcase',
    keywords: ['managers', 'gestionnaires', 'consulter', 'voir'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'tab-manager' },
  },
  {
    id: 'menu-admins-create-admin',
    label: 'Créer un collaborateur',
    type: 'create',
    route: '/admins?create=true&role=admin',
    icon: 'UserPlus',
    keywords: ['administrateur', 'admin', 'créer', 'ajouter', 'nouveau', 'collaborateur'],
    resource: 'admins',
    requiresWrite: true,
    excludeIfSingleAdminTab: true,
  },
  {
    id: 'menu-admins-create-superadmin',
    label: 'Créer un super-administrateur',
    type: 'create',
    route: '/admins?create=true&role=admin',
    icon: 'UserPlus',
    keywords: ['super', 'administrateur', 'superadmin', 'créer', 'ajouter', 'nouveau'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'option-create-admin' },
  },
  {
    id: 'menu-admins-create-coordinator',
    label: 'Créer un coordinateur',
    type: 'create',
    route: '/admins?create=true&role=coordinator',
    icon: 'UserPlus',
    keywords: ['coordinateur', 'coordinator', 'créer', 'ajouter', 'nouveau'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'option-create-coordinator' },
  },
  {
    id: 'menu-admins-create-manager',
    label: 'Créer un manager',
    type: 'create',
    route: '/admins?create=true&role=manager',
    icon: 'UserPlus',
    keywords: ['manager', 'gestionnaire', 'créer', 'ajouter', 'nouveau'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'option-create-manager' },
  },
  {
    id: 'menu-admins-view-coordinator-remote',
    label: 'Consulter les coordinateurs distanciel',
    type: 'view',
    route: '/admins?tab=coordinator_remote',
    icon: 'UserCog',
    keywords: ['coordinateurs', 'coordinators', 'remote', 'distanciel', 'consulter', 'voir'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'tab-coordinator' },
  },
  {
    id: 'menu-admins-view-manager-remote',
    label: 'Consulter les managers distanciel',
    type: 'view',
    route: '/admins?tab=manager_remote',
    icon: 'Briefcase',
    keywords: ['managers', 'gestionnaires', 'remote', 'distanciel', 'consulter', 'voir'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'tab-manager' },
  },
  {
    id: 'menu-admins-view-recruiter-remote',
    label: 'Consulter les recruteurs distanciel',
    type: 'view',
    route: '/admins?tab=recruiter_remote',
    icon: 'UserSearch',
    keywords: ['recruteurs', 'recruiters', 'remote', 'distanciel', 'consulter', 'voir'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'tab-recruiter' },
  },
  {
    id: 'menu-admins-create-coordinator-remote',
    label: 'Créer un coordinateur distanciel',
    type: 'create',
    route: '/admins?create=true&role=coordinator_remote',
    icon: 'UserPlus',
    keywords: [
      'coordinateur',
      'coordinator',
      'remote',
      'distanciel',
      'créer',
      'ajouter',
      'nouveau',
    ],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'option-create-coordinator' },
  },
  {
    id: 'menu-admins-create-manager-remote',
    label: 'Créer un manager distanciel',
    type: 'create',
    route: '/admins?create=true&role=manager_remote',
    icon: 'UserPlus',
    keywords: ['manager', 'gestionnaire', 'remote', 'distanciel', 'créer', 'ajouter', 'nouveau'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'option-create-manager' },
  },
  {
    id: 'menu-admins-create-recruiter-remote',
    label: 'Créer un recruteur distanciel',
    type: 'create',
    route: '/admins?create=true&role=recruiter_remote',
    icon: 'UserPlus',
    keywords: ['recruteur', 'recruiter', 'remote', 'distanciel', 'créer', 'ajouter', 'nouveau'],
    resource: 'admins',
    requiredElement: { interface: 'admins', elementId: 'option-create-recruiter' },
  },
  {
    id: 'menu-establishments-view',
    label: 'Consulter les établissements',
    type: 'view',
    route: '/establishments',
    icon: 'Building',
    keywords: ['établissements', 'establishments', 'consulter', 'voir'],
    resource: 'establishments',
  },
  {
    id: 'menu-establishments-create',
    label: 'Ajouter un établissement',
    type: 'create',
    route: '/establishments?create=true',
    icon: 'Plus',
    keywords: ['établissement', 'establishment', 'ajouter', 'créer', 'nouveau'],
    resource: 'establishments',
    requiresWrite: true,
  },
  {
    id: 'menu-reports-view',
    label: 'Consulter les comptes rendus',
    type: 'view',
    route: '/reports',
    icon: 'FileText',
    keywords: ['comptes', 'rendus', 'reports', 'consulter', 'voir', 'cr'],
    resource: 'reports',
  },
  {
    id: 'menu-reports-create',
    label: 'Créer un compte rendu',
    type: 'create',
    route: '/reports?create=true',
    icon: 'FilePlus',
    keywords: ['compte', 'rendu', 'report', 'créer', 'ajouter', 'nouveau', 'cr'],
    resource: 'reports',
    requiresWrite: true,
    hidden: true,
  },
  {
    id: 'menu-vsc-reports-view',
    label: 'Consulter les rapports VSC',
    type: 'view',
    route: '/vscReports',
    icon: 'ClipboardList',
    keywords: ['vsc', 'reports', 'rapports', 'consulter', 'voir'],
    resource: 'vscReports',
  },
  {
    id: 'menu-subjects-view',
    label: 'Consulter les matières',
    type: 'view',
    route: '/subjects',
    icon: 'BookOpen',
    keywords: ['matières', 'subjects', 'consulter', 'voir'],
    resource: 'subjects',
  },
  {
    id: 'menu-subjects-create',
    label: 'Ajouter une matière',
    type: 'create',
    route: '/subjects?create=true',
    icon: 'Plus',
    keywords: ['matière', 'subject', 'ajouter', 'créer', 'nouvelle'],
    resource: 'subjects',
    requiresWrite: true,
  },
  {
    id: 'menu-levels-view',
    label: 'Consulter les niveaux',
    type: 'view',
    route: '/levels',
    icon: 'TrendingUp',
    keywords: ['niveaux', 'levels', 'consulter', 'voir'],
    resource: 'levels',
  },
  {
    id: 'menu-levels-create',
    label: 'Ajouter un niveau',
    type: 'create',
    route: '/levels?create=true',
    icon: 'Plus',
    keywords: ['niveau', 'level', 'ajouter', 'créer', 'nouveau'],
    resource: 'levels',
    requiresWrite: true,
  },
  {
    id: 'menu-programs-view',
    label: 'Consulter les programmes',
    type: 'view',
    route: '/programs',
    icon: 'FolderTree',
    keywords: ['programmes', 'programs', 'consulter', 'voir'],
    resource: 'programs',
  },
  {
    id: 'menu-programs-create',
    label: 'Ajouter un programme',
    type: 'create',
    route: '/programs?create=true',
    icon: 'Plus',
    keywords: ['programme', 'program', 'ajouter', 'créer', 'nouveau'],
    resource: 'programs',
    requiresWrite: true,
  },
  {
    id: 'menu-school-years-view',
    label: 'Consulter les années scolaires',
    type: 'view',
    route: '/schoolYears',
    icon: 'CalendarRange',
    keywords: ['années', 'scolaires', 'school', 'years', 'consulter', 'voir'],
    resource: 'schoolYears',
  },
  {
    id: 'menu-school-years-create',
    label: 'Ajouter une année scolaire',
    type: 'create',
    route: '/schoolYears?create=true',
    icon: 'Plus',
    keywords: ['année', 'scolaire', 'school', 'year', 'ajouter', 'créer', 'nouvelle'],
    resource: 'schoolYears',
    requiresWrite: true,
  },
  {
    id: 'menu-activity-types-view',
    label: "Consulter les types d'activité",
    type: 'view',
    route: '/activityTypes',
    icon: 'Grid3x3',
    keywords: ['types', 'activité', 'activity', 'types', 'consulter', 'voir'],
    resource: 'activityTypes',
  },
  {
    id: 'menu-activity-types-create',
    label: "Ajouter un type d'activité",
    type: 'create',
    route: '/activityTypes?create=true',
    icon: 'Plus',
    keywords: ['type', 'activité', 'activity', 'type', 'ajouter', 'créer', 'nouveau'],
    resource: 'activityTypes',
    requiresWrite: true,
  },
  {
    id: 'menu-activities-view',
    label: 'Consulter les activités',
    type: 'view',
    route: '/activities',
    icon: 'Sparkles',
    keywords: ['activités', 'activities', 'consulter', 'voir'],
    resource: 'activities',
  },
  {
    id: 'menu-activities-create',
    label: 'Ajouter une activité',
    type: 'create',
    route: '/activities?create=true',
    icon: 'Plus',
    keywords: ['activité', 'activity', 'ajouter', 'créer', 'nouvelle'],
    resource: 'activities',
    requiresWrite: true,
  },
  {
    id: 'menu-holidays-view',
    label: 'Consulter les vacances',
    type: 'view',
    route: '/holidays',
    icon: 'Palmtree',
    keywords: ['vacances', 'holidays', 'congés', 'consulter', 'voir'],
    resource: 'holidays',
  },
  {
    id: 'menu-holidays-create',
    label: 'Ajouter des vacances',
    type: 'create',
    route: '/holidays?create=true',
    icon: 'Plus',
    keywords: ['vacances', 'holidays', 'congés', 'ajouter', 'créer', 'nouvelles'],
    resource: 'holidays',
    requiresWrite: true,
  },
];
